import React, { useState, useEffect } from "react";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { FormInput, FormSelect, FormButton } from "./FormComponents";
import { getTodayString } from "../utils/dateUtils";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { createReminder } from "../store/slices/remindersSlice";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { useToastContext } from "./ToastContext";

const AddReminder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const { addToast } = useToastContext();
  const userProfile = useAppSelector((state) => state.user.profile);
  const { isDark, setTheme } = useTheme();
  const initials = userProfile?.name
    ? userProfile.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (userProfile?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // Get date from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const prefilledDate = queryParams.get("date");

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    date: prefilledDate || getTodayString(),
    time: "",
    recurring: "" as "daily" | "weekly" | "monthly" | "",
  });

  const recurringOptions = [
    { value: "daily", label: t("addReminder.daily") },
    { value: "weekly", label: t("addReminder.weekly") },
    { value: "monthly", label: t("addReminder.monthly") },
  ];

  const trimmedTitle = formData.title.trim();
  const selectedRecurringOption = recurringOptions.find(
    (option) => option.value === formData.recurring,
  );
  const reminderSummary = formData.recurring
    ? t("addReminder.helpTextRecurring", {
        recurring: selectedRecurringOption?.label ?? formData.recurring,
      })
    : t("addReminder.helpTextOneTime");

  useEffect(() => {
    trackPageVisit("add_reminder");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      return;
    }

    setLoading(true);

    try {
      // Dispatch createReminder action to Redux store
      const result = await dispatch(
        createReminder({
          title: formData.title.trim(),
          date: formData.date,
          time: formData.time || undefined,
          recurring: formData.recurring || undefined,
        }),
      ).unwrap();

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }

      // Track reminder creation event
      trackUserInteraction(
        "create_reminder",
        "reminder_management",
        result?.id || "unknown",
      );

      // Navigate back to calendar
      navigate("/app");
    } catch (error) {
      console.error("Error saving reminder:", error);
      // TODO: Show error message to user
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/app");
  };

  return (
    <>
      <MetaHead
        title={`${t("addReminder.title")} - ${t("app.name")}`}
        description={t("addReminder.description") as string}
        keywords={t("addReminder.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("addReminder.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("addReminder.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            {/* Form Content */}
            <div className="max-w-5xl mx-auto pb-8 space-y-6">
              <div className="glass-card rounded-xl border border-[#E23F5D]/20 dark:border-[#E23F5D]/30 bg-gradient-to-br from-[#E23F5D]/[0.05] to-transparent dark:from-[#E23F5D]/[0.08] p-5 lg:p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#E23F5D]/20">
                      <svg
                        className="h-5 w-5 text-[#E23F5D]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className="mb-1 text-[19px] font-semibold text-neutral-900 dark:text-neutral-100">
                        {t("addReminder.headerTitle")}
                      </h2>
                      <p className="text-[17px] text-neutral-600 dark:text-neutral-400">
                        {t("addReminder.description")}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#E23F5D]/15 bg-white/80 px-3 py-1.5 text-[16px] font-medium text-neutral-700 dark:border-[#E23F5D]/20 dark:bg-white/[0.05] dark:text-neutral-200">
                      <span className="h-2 w-2 rounded-full bg-[#E23F5D]" />
                      <span>{t("addReminder.reminderDate")}</span>
                      <span className="text-neutral-500 dark:text-neutral-400">
                        {formData.date}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-black/[0.07] bg-white/80 px-3 py-1.5 text-[16px] font-medium text-neutral-700 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-neutral-200">
                      <span className="h-2 w-2 rounded-full bg-[#3b5cde]" />
                      <span>{t("addReminder.repeat")}</span>
                      <span className="text-neutral-500 dark:text-neutral-400">
                        {selectedRecurringOption?.label ??
                          t("addReminder.oneTimeReminder")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.9fr)] lg:items-start">
                <div className="glass-card rounded-xl border border-black/[0.07] bg-white/70 p-5 dark:border-white/[0.08] dark:bg-white/[0.03] lg:p-6">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label
                        htmlFor="title"
                        className="mb-2.5 block text-[17px] font-medium text-neutral-900 dark:text-neutral-100"
                      >
                        {t("addReminder.reminderTitle")}
                        <span className="ml-1 text-[#E23F5D]">*</span>
                      </label>
                      <FormInput
                        id="title"
                        label=""
                        value={formData.title}
                        onChange={(value) =>
                          setFormData({ ...formData, title: value })
                        }
                        placeholder={t("addReminder.placeholder") as string}
                      />
                      <p className="mt-1.5 text-[15px] text-neutral-500 dark:text-neutral-400">
                        {formData.title.length}/100
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="date"
                          className="mb-2.5 block text-[17px] font-medium text-neutral-900 dark:text-neutral-100"
                        >
                          {t("addReminder.reminderDate")}
                          <span className="ml-1 text-[#E23F5D]">*</span>
                        </label>
                        <FormInput
                          id="date"
                          label=""
                          type="date"
                          value={formData.date}
                          onChange={(value) =>
                            setFormData({ ...formData, date: value })
                          }
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="time"
                          className="mb-2.5 block text-[17px] font-medium text-neutral-900 dark:text-neutral-100"
                        >
                          {t("addReminder.reminderTime")}
                        </label>
                        <FormInput
                          id="time"
                          label=""
                          type="time"
                          value={formData.time}
                          onChange={(value) =>
                            setFormData({ ...formData, time: value })
                          }
                          placeholder={
                            t("addReminder.timePlaceholder") as string
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="recurring"
                        className="mb-2.5 block text-[17px] font-medium text-neutral-900 dark:text-neutral-100"
                      >
                        {t("addReminder.repeat")}
                      </label>
                      <FormSelect
                        id="recurring"
                        label=""
                        value={formData.recurring}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            recurring: value as "daily" | "weekly" | "monthly",
                          })
                        }
                        options={recurringOptions}
                        placeholder={t("addReminder.oneTimeReminder") as string}
                      />
                    </div>

                    <div className="flex flex-col-reverse gap-3 border-t border-black/[0.07] pt-5 dark:border-white/[0.08] sm:flex-row">
                      <div className="flex-1">
                        <FormButton
                          type="button"
                          variant="secondary"
                          onClick={handleCancel}
                          disabled={loading}
                          size="lg"
                        >
                          {t("addReminder.cancel")}
                        </FormButton>
                      </div>

                      <div className="flex-1">
                        <FormButton
                          type="submit"
                          variant="primary"
                          loading={loading}
                          disabled={!trimmedTitle}
                          size="lg"
                        >
                          {loading
                            ? t("addReminder.saving")
                            : t("addReminder.saveReminder")}
                        </FormButton>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border border-black/[0.07] bg-white px-4 py-4 dark:border-white/[0.08] dark:bg-white/[0.04]">
                    <p className="text-[15px] font-medium uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">
                      {t("addReminder.headerTitle")}
                    </p>
                    <p className="mt-2 text-[22px] font-semibold tracking-tight text-neutral-900 dark:text-white">
                      {trimmedTitle || t("addReminder.placeholder")}
                    </p>
                    <div className="mt-4 grid gap-3">
                      <div className="rounded-lg border border-black/[0.06] bg-[#E23F5D]/[0.05] px-3 py-3 dark:border-white/[0.06] dark:bg-[#E23F5D]/[0.08]">
                        <p className="text-[15px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                          {t("addReminder.reminderTime")}
                        </p>
                        <p className="mt-1 text-[18px] font-medium text-neutral-800 dark:text-neutral-100">
                          {formData.time || t("addReminder.timePlaceholder")}
                        </p>
                      </div>
                      <div className="rounded-lg border border-black/[0.06] bg-black/[0.02] px-3 py-3 dark:border-white/[0.06] dark:bg-white/[0.03]">
                        <p className="text-[15px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                          {t("addReminder.repeat")}
                        </p>
                        <p className="mt-1 text-[18px] font-medium text-neutral-800 dark:text-neutral-100">
                          {selectedRecurringOption?.label ??
                            t("addReminder.oneTimeReminder")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#E23F5D]/15 bg-gradient-to-br from-[#E23F5D]/[0.06] to-transparent px-4 py-4 dark:border-[#E23F5D]/20 dark:from-[#E23F5D]/[0.1]">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E23F5D]/20 text-[#E23F5D]">
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[17px] font-medium text-neutral-900 dark:text-neutral-100">
                          {reminderSummary}
                        </p>
                        <p className="mt-1 text-[16px] leading-relaxed text-neutral-600 dark:text-neutral-400">
                          {t("addReminder.reminderDate")}: {formData.date}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default AddReminder;
